import { useState, useContext } from "react";
import { useNavigate } from "@tanstack/react-router";
// Import Context
import { AlertNotificationContext } from "../contexts/AlertNotificationContext";
// Hooks
import { useMergeDraftOrders } from "../hooks/useMergeDraftOrders";
import { useCheckAPI } from "../hooks/useCheckAPI";
import { useBulkOrderMode } from "../hooks/useAuthorisation";
import { getDraftDetailPath } from "../utils/orderPaths";
// MUI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

export const MergeDraftDialog = ({
  disabled,
  selectedRows,
  setSelectedRows,
  entityLabel = "Requests",
}) => {
  const { mutate, isSuccess, data } = useMergeDraftOrders();
  const [open, setOpen] = useState(false);
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const navigate = useNavigate();
  const bulkOrderMode = useBulkOrderMode();

  // Copy below needs both "Requests" and "requests" forms.
  const lowerLabel = entityLabel.toLowerCase();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const ordersToMerge = selectedRows.join("|");

  const handleMerge = () => {
    // API call to merge draft orders
    mutate({
      data: {
        data: {
          filter: { noFilter: "" },
          order: {
            no: "",
            poNo: "",
            status: "",
            branch: "",
            requestedShippingDate: "",
            code: "",
            contact: "",
            name: "",
            address: "",
            address2: "",
            city: "",
            county: "",
            postCode: "",
            country: "",
            homeaddress: false,
            email: "",
            mobileNo: "",
            orderTotal: 0,
          },
          staff: [],
        },
      },
      ordersToMerge: ordersToMerge,
    });
    setOpen(false);
  };

  // API call success
  const successFunc = () => {
    setOpen(false);
    setSelectedRows?.([]);
    setAlertNotification({
      message: `${entityLabel} merged successfully`,
      severity: "info",
    });
    // Navigate to the draft order list
    const orderNo = data?.data?.data?.data?.data?.order?.no;
    if (!orderNo) return;
    navigate({ to: getDraftDetailPath(orderNo, bulkOrderMode) });
  };

  // API call error
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "warning",
    });

  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  return (
    <Box>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleClickOpen}
        disabled={selectedRows.length < 2 || disabled}
      >
        Merge {entityLabel}
      </Button>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={open}
        onClose={handleClose}
        aria-labelledby="close-dialog"
      >
        <DialogTitle id="merge-draft-dialog-title">
          Merge {entityLabel}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to merge the following {lowerLabel}:{" "}
            {selectedRows.join(", ")}?
          </DialogContentText>
        </DialogContent>
        <DialogContent>
          <DialogContentText>
            <strong>Note:</strong> {entityLabel} cannot contain the same staff
            member more than once. Merging {lowerLabel} will create a new draft
            order and the original {lowerLabel} will be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleMerge}>Merge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
